import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { get, size } from "lodash";
import {
  Button,
  Grid,
  Typography,
  TextField,
  Stack,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
  Rating,
} from "@mui/material";
import {  useSnackbar } from "notistack";
import { color } from "../../utils/constant";
import RHFAutocomplete from "../../components/shared/RHFAutocomplete";
import { useSelector } from "react-redux";
import { getDoctorList, getPatientList, getReviewData } from "../../data/selectors/Review/Review.selector";
import { useReviewForm } from "../../data/talons/Review/useReviewForm";
import MaterialUIPickers from "../../components/shared/DesktopDatePicker";

export default function ReviewForm(props) {
  const { editId } = props;
  const editItem = useSelector(getReviewData);
  const { enqueueSnackbar } = useSnackbar();
  // const { fetchPatientComboList } = useReview();
  const doctorList = useSelector(getDoctorList);
  const patientList = useSelector(getPatientList);
   const { fetchDoctorComboList,
      findDoctorFromLabel,
       fetchPatientComboList,
      findpatientFromLabel,
      DoctorId,
      PatientId,
      updateReviewData,
      status,
      handleStatusChange,
      DateTime,
      handleDateTime,
      rating,
      handleRatingChange,
      comment,
      reviewFormSchema,
                 handleCommentChange,
                  isRecommend,
                   handleRecommendationChange } = useReviewForm({
                    existingDoctorId: get(editItem, "doctor_id", ""), 
                    existingPatientId: get(editItem, "patient_id", ""), 
                    existingStatus: get(editItem, "status", ""),
                    existingRating: get(editItem, "rating", 1),
                    existingComment: get(editItem, "comment", ""),
                    existingIsRecommend: get(editItem, "is_recommend", false),
                    existingDateTime: get(editItem, "date_time", null)
                  });
  
     useEffect(() => {
    fetchDoctorComboList();
    fetchPatientComboList();
  }, []);
  
  const defaultValues = useMemo(() => ({
    name: get(editItem, "doctor_name", ""),
    patientName: get(editItem, "patient_name", ""),
    comment: get(editItem, "comment", ""),
    // recommend: get(editItem, "is_recommend_val", ""),
  }));

  const { register, control, handleSubmit,
    formState: { errors },
     getValues,
      setValue } = useForm({
    resolver: joiResolver(reviewFormSchema),
    defaultValues,
  });

    
  const onUserSubmit = useCallback(async () => {
    try {
      const reviewId = get(editItem, "id", null);
      if (!reviewId) {
        enqueueSnackbar("Review ID is required", {
          variant: "error",
        });
        return;
      }

      const res = await updateReviewData({
        id: reviewId,
      });
      
      if (res === true) {
        enqueueSnackbar("Review updated successfully", {
          variant: "success",
        });
        props.postSubmit();
      } else {
        enqueueSnackbar("Problem While Saving Review Data", {
          variant: "error",
        });
      }
    } catch (err) {
      console.log("Error in onUserSubmit:", err);
      enqueueSnackbar("An error occurred while saving the review", {
        variant: "error",
      });
    }
  }, [updateReviewData, editItem, props, enqueueSnackbar]);

  return (
    <React.Fragment>
      <Typography variant="h5" gutterBottom>
        Review Form
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6}>
          <RHFAutocomplete
            borderRadius={"0px"}
            isRequired={true}
            name="name"
            label="Doctor Name"
            // disabled={editId ? true : false}
            freeSolo
            data={doctorList || []}
            defaultValue={get(editItem, "doctor_name", "")}
            onChange={(value) => findDoctorFromLabel(value)}

            id={doctorList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <RHFAutocomplete
            borderRadius={"0px"}
            isRequired={true}
            name="patientName"
            label="Patient Name"
            // disabled={editId ? true : false}
            freeSolo
            data={patientList || []}
            defaultValue={get(editItem, "patient_name", "")}
            onChange={(value) => findpatientFromLabel(value)}
            id={patientList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
        <MaterialUIPickers
                required
                id="dateTime"
                label="Date & Time"
                type="datetime"
                value={DateTime}
                size={"small"}
                onChange={handleDateTime}
                sx={{ width: "100%" }}
                InputLabelProps={{
                  shrink: true,
                }}
              ></MaterialUIPickers>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Typography component="legend" sx={{ mb: 1, fontSize: '0.875rem' }}>
            Rating *
          </Typography>
          <Rating
            name="rating"
            value={rating}
            onChange={(event, newValue) => {
              handleRatingChange(newValue);
            }}
            size="large"
            sx={{
              '& .MuiRating-iconFilled': {
                color: color.button,
              },
              '& .MuiRating-iconHover': {
                color: color.button,
              },
            }}
          />

        </Grid>
        <Grid item xs={12} sm={12}>
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  "&.Mui-checked": {
                    color: color.button,
                  },
                }}
                checked={isRecommend === 1}
                onChange={handleRecommendationChange}
              />
            }
            label="Would you recommend this doctor?"
          />

        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            required
            // disabled={editId ? true : false}
            id="comment"
            name="comment"
            label="Comment"
            fullWidth={true}
            variant="outlined"
            multiline
            rows={4}
            size="small"
            value={comment}
            onChange={handleCommentChange}
            inputProps={{
              maxLength: 500,
            }}
            autoComplete="false"
            error={!!errors?.comment?.message}
            helperText={errors?.comment?.message || ""}
          />
        </Grid>

        <Grid item xs={3} sm={3}>
          <Typography>
            <FormControlLabel
              sx={{ mt: -1 }}
              // disabled={editId ? true : false}
              control={
                <Checkbox
                  sx={{
                    "&.Mui-checked": {
                      color: color.button,
                    },
                  }}
                  id="is_labour"
                  checked={status}
                  onChange={handleStatusChange}
                />
              }
              label="Active"
            />
          </Typography>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={12} sm={12}>
            <Stack direction="row" justifyContent="end">
              <Button
                variant="contained"
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  backgroundColor: color.button,
                  "&:hover": {
                    backgroundColor: color.button,
                  },
                }}
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(onUserSubmit)();
                }}
              >
                Save
              </Button>
              <Button
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  borderColor: color.button,
                  color: color.button,
                  "&:hover": {
                    color: color.button,
                  },
                }}
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  props.postSubmit();
                }}
              >
                Close
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
